import React, {useCallback, useContext, useState} from 'react';

import {MessageContext} from 'src/contexts/message-context';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';
import {useShown} from '@pb/kit-hooks-use-shown';


import {useGenerateLoginLink} from './useGenerateLoginLink';

import type {GetLoginLinkResponse} from 'src/openapi/admin/models/GetLoginLinkResponse';

type Props = {
    userId: number,
    disabled: boolean,
};

const MODAL_CAPTION = 'Generate Login Link';
const MODAL_WIDTH = 450;

export const GenerateLoginLink = ({userId, disabled}: Props) => {
    const {setSuccessMessage} = useContext(MessageContext);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const {loginActionSubmit} = useEventTracking();
    const {shown, onClick, onClose} = useShown();

    const {event, busy} = useGenerateLoginLink({
        userId,
        onSuccess: (data: GetLoginLinkResponse) => {
            loginActionSubmit({submit: true, success: true, itemAction: MODAL_CAPTION});
            navigator.clipboard.writeText(`${data.loginLink}`).then(() => {
                setSuccessMessage('Login link is copied to your clipboard');
            });
            onClose();
        },
        onFail: (error: string) => {
            loginActionSubmit({submit: true, success: false, itemAction: MODAL_CAPTION});
            setErrorMessage(error);
        },
    });

    const closeConfirmDialog = useCallback(() => {
        onClose();
        loginActionSubmit({submit: false, success: true, itemAction: MODAL_CAPTION});
    }, [loginActionSubmit, onClose]);


    return (
        <div>
            <Button disabled={disabled} onClick={onClick} variant="Type.SECONDARY">
                Generate Login Link
            </Button>
            <Dialog
                shown={shown}
                setShown={closeConfirmDialog}>
                <DialogHeader onClose={closeConfirmDialog}>
                    {MODAL_CAPTION}
                </DialogHeader>
                <DialogMain style={{padding: 24, maxWidth: MODAL_WIDTH}}>
                    {errorMessage ? <Message color="error">{errorMessage}</Message> : null}
                    <p>
                        Login link will be generated and copied to your clipboard.
                        You can then send it to the user. When a user clicks on this link,
                        they will be taken to a Password Reset page
                        and must change their password before logging in.
                    </p>
                    <p>Note: Login links are one-time use only and expire after 24 hours.</p>
                    <p>Do you want to generate login link?</p>
                </DialogMain>
                <DialogFooter>
                    <Group>
                        <Button
                            busy={busy}
                            disabled={disabled}
                            variant="Type.SECONDARY"
                            onClick={() => event()}>
                            Generate
                        </Button>
                        <Button variant="Type.SECONDARY" onClick={closeConfirmDialog}>
                            Cancel
                        </Button>
                    </Group>
                </DialogFooter>
            </Dialog>
        </div>
    );
};
